<!-- Exchange Rate History Dashboard -->
<div class="p-6 bg-gray-100 min-h-screen">
  <div class="flex flex-wrap justify-between items-center gap-4 mb-6">
    <div>
      <h1 class="text-3xl font-bold text-gray-800">📊 Histórico de Tasas de Cambio</h1>
      <p class="text-gray-500 mt-1">Análisis completo de cotizaciones y tendencias de mercado</p>
    </div>
    <div class="flex items-center gap-2">
      <button 
        (click)="refreshRates()"
        [disabled]="isLoading"
        class="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Actualizar Tasas
      </button>
      <a routerLink="/config/exchange-rate" 
         class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center gap-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        Configuración
      </a>
    </div>
  </div>

  <!-- KPI Cards -->
  <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
    <!-- Tasa Más Alta -->
    <div class="bg-green-50 border border-green-300 rounded-2xl p-5 shadow-sm">
      <div class="flex items-center justify-between">
        <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
        <p class="text-sm text-green-700 font-medium">Tasa Más Alta</p>
      </div>
      <p class="text-2xl font-bold text-green-800 mt-2">{{ formatRate(highestRate) }}</p>
      <p class="text-xs text-green-600 mt-1">Máximo del período</p>
    </div>

    <!-- Tasa Más Baja -->
    <div class="bg-red-50 border border-red-300 rounded-2xl p-5 shadow-sm">
      <div class="flex items-center justify-between">
        <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17h8m0 0v-8m0 8l-8-8-4 4-6-6" />
        </svg>
        <p class="text-sm text-red-700 font-medium">Tasa Más Baja</p>
      </div>
      <p class="text-2xl font-bold text-red-800 mt-2">{{ formatRate(lowestRate) }}</p>
      <p class="text-xs text-red-600 mt-1">Mínimo del período</p>
    </div>

    <!-- Tasa Promedio -->
    <div class="bg-blue-50 border border-blue-300 rounded-2xl p-5 shadow-sm">
      <div class="flex items-center justify-between">
        <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <p class="text-sm text-blue-700 font-medium">Tasa Promedio</p>
      </div>
      <p class="text-2xl font-bold text-blue-800 mt-2">{{ formatRate(averageRate) }}</p>
      <p class="text-xs text-blue-600 mt-1">Media del período</p>
    </div>

    <!-- Variación Total -->
    <div class="bg-purple-50 border border-purple-300 rounded-2xl p-5 shadow-sm">
      <div class="flex items-center justify-between">
        <svg class="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
        </svg>
        <p class="text-sm text-purple-700 font-medium">Variación Total</p>
      </div>
      <p class="text-2xl font-bold text-purple-800 mt-2">{{ totalVariation.toFixed(2) }}%</p>
      <p class="text-xs text-purple-600 mt-1">Rango de fluctuación</p>
    </div>
  </div>

  <!-- Gráfico de Tendencias -->
  <div class="bg-white p-6 rounded-2xl shadow-md border border-gray-200 mb-8">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-semibold text-gray-800">📈 Evolución de Tasas de Cambio</h2>
      <div class="text-sm text-gray-500">
        <span>Última actualización: </span>
        <span class="font-medium">{{ formatDate(lastUpdate) }}</span>
      </div>
    </div>
    
    <div *ngIf="chartSeries && chartSeries.length > 0; else noChartData">
      <apx-chart
        [series]="chartSeries"
        [chart]="chartOptions"
        [xaxis]="chartXAxis"
        [yaxis]="chartYAxis"
        [dataLabels]="chartDataLabels"
        [stroke]="chartStroke"
        [markers]="chartMarkers"
        [colors]="chartColors">
      </apx-chart>
    </div>
    
    <ng-template #noChartData>
      <div class="flex flex-col items-center justify-center py-12 text-gray-500">
        <svg class="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <p class="text-lg font-medium">No hay datos para mostrar</p>
        <p class="text-sm">Aplicar filtros para ver las tendencias de tasas</p>
      </div>
    </ng-template>
  </div>

  <!-- Filtros de Búsqueda -->
  <div class="bg-white rounded-2xl border border-gray-200 shadow-md p-6 mb-8">
    <h3 class="text-lg font-semibold text-gray-800 mb-4">🔍 Filtros de Búsqueda</h3>
    
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <!-- Moneda Origen -->
      <div>
        <label for="fromCurrency" class="block text-sm font-medium text-gray-700 mb-2">
          Moneda Origen
        </label>
        <select 
          id="fromCurrency"
          [(ngModel)]="filters.fromCurrency"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          <option value="">Todas las monedas</option>
          <option *ngFor="let currency of currencies" [value]="currency.code">
            {{ currency.code }} - {{ currency.name }}
          </option>
        </select>
      </div>

      <!-- Moneda Destino -->
      <div>
        <label for="toCurrency" class="block text-sm font-medium text-gray-700 mb-2">
          Moneda Destino
        </label>
        <select 
          id="toCurrency"
          [(ngModel)]="filters.toCurrency"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          <option value="">Todas las monedas</option>
          <option *ngFor="let currency of currencies" [value]="currency.code">
            {{ currency.code }} - {{ currency.name }}
          </option>
        </select>
      </div>

      <!-- Fecha Desde -->
      <div>
        <label for="dateFrom" class="block text-sm font-medium text-gray-700 mb-2">
          Fecha Desde
        </label>
        <input 
          type="date"
          id="dateFrom"
          [(ngModel)]="filters.dateFrom"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
      </div>

      <!-- Fecha Hasta -->
      <div>
        <label for="dateTo" class="block text-sm font-medium text-gray-700 mb-2">
          Fecha Hasta
        </label>
        <input 
          type="date"
          id="dateTo"
          [(ngModel)]="filters.dateTo"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
      </div>
    </div>

    <!-- Botones de Filtro -->
    <div class="flex justify-end gap-3 mt-6">
      <button 
        (click)="clearFilters()"
        class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition">
        Limpiar Filtros
      </button>
      <button 
        (click)="applyFilters()"
        [disabled]="isLoading"
        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
        Aplicar Filtros
      </button>
    </div>
  </div>

  <!-- Tabla de Resultados -->
  <div class="bg-white rounded-2xl border border-gray-200 shadow-md overflow-hidden">
    
    <!-- Header de la tabla con información -->
    <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
      <div class="flex justify-between items-center">
        <h3 class="text-lg font-semibold text-gray-800">📋 Datos Detallados</h3>
        <div class="flex items-center gap-4">
          <!-- Información de registros -->
          <span class="text-sm text-gray-600">{{ recordsRangeText }}</span>
          
          <!-- Selector de tamaño de página -->
          <div class="flex items-center gap-2">
            <label for="pageSize" class="text-sm text-gray-600">Mostrar:</label>
            <select 
              id="pageSize"
              [(ngModel)]="pageSize"
              (ngModelChange)="onPageSizeChange($event)"
              class="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option *ngFor="let size of pageSizeOptions" [value]="size">{{ size }}</option>
            </select>
            <span class="text-sm text-gray-600">registros</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoading" class="flex justify-center items-center py-12">
      <div class="flex items-center gap-3">
        <svg class="animate-spin h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="text-gray-600">Cargando datos...</span>
      </div>
    </div>

    <!-- No Data State -->
    <div *ngIf="!isLoading && !hasData" class="text-center py-12">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">No hay datos disponibles</h3>
      <p class="mt-1 text-sm text-gray-500">No se encontraron tasas de cambio con los filtros aplicados.</p>
    </div>

    <!-- Tabla de datos -->
    <div *ngIf="!isLoading && hasData" class="overflow-x-auto">
      <table class="w-full">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Monedas
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Tasa
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Fecha Efectiva
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Fuente
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Última Actualización
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Estado
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr *ngFor="let rate of exchangeRates" class="hover:bg-gray-50 transition">
            
            <!-- Monedas -->
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="flex items-center">
                <div class="flex items-center space-x-2">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {{ rate.fromCurrency.code }}
                  </span>
                  <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {{ rate.toCurrency.code }}
                  </span>
                </div>
              </div>
              <div class="text-xs text-gray-500 mt-1">
                {{ getCurrencyName(rate.fromCurrency.code || '') }} → {{ getCurrencyName(rate.toCurrency.code || '') }}
              </div>
            </td>

            <!-- Tasa -->
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm font-mono font-medium text-gray-900 bg-gray-50 px-2 py-1 rounded">
                {{ formatRate(rate.rate) }}
              </div>
            </td>

            <!-- Fecha Efectiva -->
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm text-gray-900">
                {{ formatEffectiveDate(rate.effectiveDate) }}
              </div>
            </td>

            <!-- Fuente -->
            <td class="px-6 py-4 whitespace-nowrap">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    [ngClass]="getSourceBadgeColor(rate.source)">
                {{ rate.source }}
              </span>
            </td>

            <!-- Última Actualización -->
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm text-gray-900">
                {{ formatDate(rate.retrievedAt) }}
              </div>
            </td>

            <!-- Estado -->
            <td class="px-6 py-4 whitespace-nowrap">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    [ngClass]="rate.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                <span class="w-1.5 h-1.5 rounded-full mr-1.5"
                      [ngClass]="rate.isActive ? 'bg-green-400' : 'bg-red-400'"></span>
                {{ rate.isActive ? 'Activa' : 'Inactiva' }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginación -->
    <div *ngIf="!isLoading && hasData && totalPages > 1" class="px-6 py-4 border-t border-gray-200 bg-gray-50">
      <div class="flex items-center justify-between">
        
        <!-- Información de páginas -->
        <div class="text-sm text-gray-700">
          Página {{ currentPage }} de {{ totalPages }}
        </div>

        <!-- Controles de paginación -->
        <div class="flex items-center space-x-2">
          
          <!-- Botón anterior -->
          <button 
            (click)="onPageChange(currentPage - 1)"
            [disabled]="currentPage === 1"
            class="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-md">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <!-- Números de página -->
          <div class="flex space-x-1">
            <button 
              *ngFor="let page of getVisiblePages()"
              (click)="page > 0 ? onPageChange(page) : null"
              [disabled]="page === -1"
              [ngClass]="{
                'bg-blue-600 text-white': page === currentPage,
                'bg-white text-gray-700 hover:bg-gray-50': page !== currentPage && page > 0,
                'bg-white text-gray-400 cursor-default': page === -1
              }"
              class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md">
              {{ page === -1 ? '...' : page }}
            </button>
          </div>

          <!-- Botón siguiente -->
          <button 
            (click)="onPageChange(currentPage + 1)"
            [disabled]="currentPage === totalPages"
            class="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-md">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>

</div>